import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import {
  createWebhook,
  updateWebhook,
  getWebhookEvents,
} from "../../http/webhookAPI";

const WebhookDialog = ({ open, webhook, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    url: "",
    type: "",
    requiredFlag: "",
    successFlag: "",
    headers: "{}",
    data: "{}",
    isActive: true,
  });

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingEvents, setLoadingEvents] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (webhook) {
      setFormData({
        url: webhook.url || "",
        type: webhook.type || "",
        requiredFlag: webhook.requiredFlag || "",
        successFlag: webhook.successFlag || "",
        headers: JSON.stringify(webhook.headers || {}, null, 2),
        data: JSON.stringify(webhook.data || {}, null, 2),
        isActive: webhook.isActive !== undefined ? webhook.isActive : true,
      });

      if (webhook.type) {
        const event = events.find((e) => e.type === webhook.type);
        setSelectedEvent(event);
      }
    } else {
      setFormData({
        url: "",
        type: "",
        requiredFlag: "",
        successFlag: "",
        headers: "{}",
        data: "{}",
        isActive: true,
      });
      setSelectedEvent(null);
    }
  }, [webhook, events]);

  const fetchEvents = async () => {
    try {
      setLoadingEvents(true);
      const data = await getWebhookEvents();
      setEvents(data);
    } catch (error) {
      console.error("Ошибка при загрузке событий:", error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleEventChange = (eventType) => {
    setFormData({ ...formData, type: eventType });
    const event = events.find((e) => e.type === eventType);
    setSelectedEvent(event);
  };

  const validateJSON = (field, value) => {
    try {
      JSON.parse(value);
      setErrors((prev) => ({ ...prev, [field]: null }));
      return true;
    } catch (e) {
      setErrors((prev) => ({ ...prev, [field]: "Некорректный JSON" }));
      return false;
    }
  };

  const handleSave = async () => {
    const newErrors = {};

    if (!formData.url) {
      newErrors.url = "URL обязателен";
    }

    if (!formData.type) {
      newErrors.type = "Выберите тип события";
    }

    if (!validateJSON("headers", formData.headers)) {
      newErrors.headers = "Некорректный JSON";
    }

    if (!validateJSON("data", formData.data)) {
      newErrors.data = "Некорректный JSON";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        url: formData.url,
        type: formData.type,
        headers: JSON.parse(formData.headers),
        data: JSON.parse(formData.data),
        isActive: formData.isActive,
      };

      // Добавляем опциональные поля только если они заполнены
      if (formData.requiredFlag) {
        payload.requiredFlag = formData.requiredFlag;
      }
      if (formData.successFlag) {
        payload.successFlag = formData.successFlag;
      }

      if (webhook) {
        await updateWebhook(webhook.id, payload);
      } else {
        await createWebhook(payload);
      }

      onSave();
    } catch (error) {
      setErrors({
        general:
          error.response?.data?.message || "Ошибка при сохранении веб-хука",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {webhook ? "Редактировать веб-хук" : "Создать веб-хук"}
      </DialogTitle>

      <DialogContent>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            select
            label="Тип события"
            value={formData.type}
            onChange={(e) => handleEventChange(e.target.value)}
            error={!!errors.type}
            helperText={errors.type}
            disabled={loadingEvents}
            fullWidth
          >
            {loadingEvents ? (
              <MenuItem disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              events.map((event) => (
                <MenuItem key={event.type} value={event.type}>
                  {event.description} ({event.type})
                </MenuItem>
              ))
            )}
          </TextField>

          {selectedEvent && (
            <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" gutterBottom>
                Доступные переменные для события "{selectedEvent.description}":
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {selectedEvent.variables.map((variable) => (
                  <Chip
                    key={variable}
                    label={`{{${variable}}}`}
                    size="small"
                    onClick={() => copyToClipboard(`{{${variable}}}`)}
                    icon={<ContentCopyIcon fontSize="small" />}
                    sx={{ cursor: "pointer" }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Нажмите на переменную, чтобы скопировать её в буфер обмена
              </Typography>
            </Paper>
          )}

          <TextField
            label="URL"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            error={!!errors.url}
            helperText={errors.url || "URL, на который будет отправляться запрос"}
            fullWidth
            placeholder="https://example.com/webhook"
          />

          <TextField
            label="Требуемый флаг"
            value={formData.requiredFlag}
            onChange={(e) => setFormData({ ...formData, requiredFlag: e.target.value })}
            fullWidth
            placeholder="hasAgreedToTerms"
            helperText="Хук выполнится только если этот флаг установлен в true"
          />

          <TextField
            label="Флаг успеха"
            value={formData.successFlag}
            onChange={(e) => setFormData({ ...formData, successFlag: e.target.value })}
            fullWidth
            placeholder="welcomeEmailSent"
            helperText="Этот флаг будет true после успешного выполнения хука"
          />

          <TextField
            label="Заголовки (JSON)"
            value={formData.headers}
            onChange={(e) => {
              setFormData({ ...formData, headers: e.target.value });
              validateJSON("headers", e.target.value);
            }}
            error={!!errors.headers}
            helperText={
              errors.headers ||
              'Пример: {"Authorization": "Bearer token", "Content-Type": "application/json"}'
            }
            multiline
            rows={4}
            fullWidth
            placeholder='{"Authorization": "Bearer token"}'
          />

          <TextField
            label="Шаблон данных (JSON)"
            value={formData.data}
            onChange={(e) => {
              setFormData({ ...formData, data: e.target.value });
              validateJSON("data", e.target.value);
            }}
            error={!!errors.data}
            helperText={
              errors.data ||
              'Используйте плейсхолдеры в формате {{variableName}}. Пример: {"user_id": "{{id}}", "name": "{{name}}"}'
            }
            multiline
            rows={6}
            fullWidth
            placeholder='{"user_id": "{{id}}", "name": "{{name}}"}'
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
            }
            label="Активен"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Отмена
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {webhook ? "Сохранить" : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebhookDialog;
